import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { errMsg, toCents, uid, money } from "@/lib/api";

const blank = () => ({ name: "", date: "", time: "19:00", location: "", description: "", capacity: 50, active: true,
  ticket_types: [{ id: uid(), name: "General", price_cents: 0, tax_exempt: false }] });

const EventForm = ({ initial, onSave, onCancel }) => {
  const [e, setE] = useState(initial);
  const set = (k) => (x) => setE({ ...e, [k]: x.target.value });
  const updT = (i, t) => setE({ ...e, ticket_types: e.ticket_types.map((x, j) => (j === i ? t : x)) });
  return (
    <div className="card-lg grid gap-3 p-5" data-testid="event-form">
      <Input placeholder="Event name" value={e.name} onChange={set("name")} data-testid="event-name-input" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Input type="date" value={e.date} onChange={set("date")} data-testid="event-date-input" />
        <Input type="time" value={e.time} onChange={set("time")} data-testid="event-time-input" />
        <Input placeholder="Capacity" inputMode="numeric" value={e.capacity} onChange={set("capacity")} data-testid="event-capacity-input" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={e.active} onCheckedChange={(v) => setE({ ...e, active: v })} /> Live</label>
      </div>
      <Input placeholder="Location" value={e.location} onChange={set("location")} data-testid="event-location-input" />
      <Input placeholder="Description" value={e.description} onChange={set("description")} />
      <div className="eyebrow mt-2">Ticket types</div>
      {e.ticket_types.map((t, i) => (
        <div key={t.id} className="flex flex-wrap items-center gap-2">
          <Input className="flex-1" value={t.name} onChange={(x) => updT(i, { ...t, name: x.target.value })} data-testid="ticket-name-input" />
          <Input className="w-24" defaultValue={(t.price_cents / 100).toFixed(2)} onBlur={(x) => updT(i, { ...t, price_cents: toCents(x.target.value) })} data-testid="ticket-price-input" />
          <label className="flex items-center gap-1 text-xs"><Switch checked={t.tax_exempt} onCheckedChange={(v) => updT(i, { ...t, tax_exempt: v })} /> Tax exempt</label>
          <button aria-label="Remove ticket" onClick={() => setE({ ...e, ticket_types: e.ticket_types.filter((_, j) => j !== i) })} className="text-danger"><Trash2 size={16} /></button>
        </div>))}
      <button className="link-lg text-left text-sm" onClick={() => setE({ ...e, ticket_types: [...e.ticket_types, { id: uid(), name: "Paid", price_cents: 1000, tax_exempt: false }] })} data-testid="add-ticket-type-btn">+ Ticket type</button>
      <div className="flex gap-2"><button className="btn-primary !py-2" onClick={() => onSave({ ...e, capacity: Number(e.capacity) })} data-testid="event-save-btn">Save event</button>
        <button className="btn-secondary !py-2" onClick={onCancel}>Cancel</button></div>
    </div>
  );
};

export default function EventsPanel({ api, venue }) {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/venues/${venue.id}/events`).then((r) => setEvents(r.data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [venue.id]);
  const save = async (e) => {
    try {
      const { id, venue_id, created_at, issued, checked_in, ...body } = e;
      if (id) await api.put(`/venues/${venue.id}/events/${id}`, body); else await api.post(`/venues/${venue.id}/events`, body);
      toast.success("Event saved"); setEditing(null); load();
    } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="mt-4 grid gap-4" data-testid="events-panel">
      {!editing && <button className="btn-primary w-fit !py-2" onClick={() => setEditing(blank())} data-testid="new-event-btn"><Plus size={16} /> New event</button>}
      {editing && <EventForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {events.map((e) => (
        <div key={e.id} className="card-lg flex flex-wrap items-center justify-between gap-3 p-5" data-testid={`event-row-${e.id}`}>
          <div>
            <div className="font-heading text-xl font-black">{e.name} {!e.active && <span className="text-xs text-muted2">(hidden)</span>}</div>
            <div className="text-sm text-muted2">{e.date} · {e.time} · {e.location}</div>
            <div className="mt-1 text-sm">{e.ticket_types.map((t) => `${t.name} ${t.price_cents ? money(t.price_cents) : "free"}`).join(" · ")}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-black" data-testid={`event-checkin-${e.id}`}>{e.checked_in} / {e.capacity}</div>
            <div className="text-xs text-muted2">checked in · {e.issued} passes issued</div>
            <div className="mt-2 flex gap-2"><Link to={`/e/${e.id}`} target="_blank" className="btn-secondary !px-3 !py-1.5 text-sm">Public page <ExternalLink size={14} /></Link>
              <button className="btn-secondary !px-3 !py-1.5 text-sm" onClick={() => setEditing(e)} data-testid={`edit-event-${e.id}`}>Edit</button></div>
          </div>
        </div>))}
    </div>
  );
}
